import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Users, UserCheck, Wallet, Clock, ArrowUpRight } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from "recharts";
import { api } from "../../lib/api";

const STATUS_COLORS = {
  New: "#5C6661", Contacted: "#3B82F6", "In Progress": "#D17B5D",
  Pending: "#F59E0B", Completed: "#1E4D3B", Rejected: "#DC2626",
};

const StatCard = ({ label, value, icon: Icon, accent }) => (
  <div className="bento-card p-6" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-start justify-between">
      <div>
        <span className="label-eyebrow text-[10px]">{label}</span>
        <div className="mt-3 text-3xl font-display font-medium text-[#121C17]">{value}</div>
      </div>
      <div className={`w-10 h-10 rounded-xl grid place-items-center ${accent}`}><Icon size={18} /></div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="p-10 text-[#5C6661]">Loading analytics…</div>;

  const statusData = Object.entries(data.by_status).map(([name, value]) => ({ name, value }));
  const serviceData = Object.entries(data.by_service).map(([name, count]) => ({ name: name.length > 16 ? name.slice(0, 16) + "…" : name, count }));

  return (
    <div className="p-4 sm:p-6 md:p-10" data-testid="admin-dashboard">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <span className="label-eyebrow">Overview</span>
          <h1 className="mt-2 text-3xl font-display font-medium tracking-tight text-[#121C17]">Dashboard</h1>
        </div>
        <Link to="/admin/leads" data-testid="dashboard-view-leads" className="text-sm text-[#1E4D3B] font-medium inline-flex items-center gap-1">
          View all leads <ArrowUpRight size={14} />
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
        <StatCard label="Total Leads" value={data.total_leads} icon={Users} accent="bg-[#1E4D3B]/10 text-[#1E4D3B]" />
        <StatCard label="Customers" value={data.total_customers} icon={UserCheck} accent="bg-[#D17B5D]/10 text-[#D17B5D]" />
        <StatCard label="Revenue" value={`₹${data.total_revenue.toLocaleString("en-IN")}`} icon={Wallet} accent="bg-emerald-100 text-emerald-700" />
        <StatCard label="Outstanding" value={`₹${data.total_due.toLocaleString("en-IN")}`} icon={Clock} accent="bg-amber-100 text-amber-700" />
        <StatCard label="Conversion" value={`${data.conversion_rate}%`} icon={TrendingUp} accent="bg-blue-100 text-blue-700" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-8">
        <div className="bento-card p-6 lg:col-span-2" data-testid="chart-by-service">
          <h3 className="font-display font-medium text-[#121C17]">Leads by service</h3>
          <div className="mt-4 h-64">
            {serviceData.length === 0 ? (
              <div className="h-full grid place-items-center text-sm text-[#5C6661]">No data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={serviceData}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#5C6661" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#5C6661" allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#1E4D3B" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bento-card p-6" data-testid="chart-by-status">
          <h3 className="font-display font-medium text-[#121C17]">By status</h3>
          <div className="mt-4 h-64">
            {data.total_leads === 0 ? (
              <div className="h-full grid place-items-center text-sm text-[#5C6661]">No leads yet</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData.filter((s) => s.value > 0)} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80}>
                    {statusData.map((s) => <Cell key={s.name} fill={STATUS_COLORS[s.name]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            {statusData.map((s) => (
              <li key={s.name} className="flex items-center gap-2 text-[#5C6661]">
                <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[s.name] }} />
                {s.name} <span className="text-[#121C17] font-medium ml-auto">{s.value}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="bento-card p-6" data-testid="recent-leads">
        <h3 className="font-display font-medium text-[#121C17] mb-4">Recent leads</h3>
        {data.recent_leads.length === 0 ? (
          <p className="text-sm text-[#5C6661]">No leads yet. Once visitors submit a form, they'll appear here.</p>
        ) : (
          <ul className="divide-y divide-[#E5E2DC]">
            {data.recent_leads.map((l) => (
              <li key={l.id} className="py-3 flex items-center gap-3" data-testid={`recent-lead-${l.id}`}>
                <div className="w-9 h-9 rounded-full bg-[#F3F1EC] grid place-items-center text-xs font-medium text-[#1E4D3B]">
                  {l.full_name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#121C17] truncate">{l.full_name}</div>
                  <div className="text-xs text-[#5C6661] truncate">{l.service_name} · {l.mobile}</div>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full ${statusPill(l.status)}`}>{l.status}</span>
                <Link to={`/admin/leads/${l.id}`} className="text-xs text-[#1E4D3B] font-medium hover:underline">Open</Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export const statusPill = (s) => ({
  "New": "pill-new",
  "Contacted": "pill-contacted",
  "In Progress": "pill-progress",
  "Pending": "pill-pending",
  "Completed": "pill-completed",
  "Rejected": "pill-rejected",
}[s] || "pill-new");
